/**
 * jaiio-header.js — mécanique de l'en-tête V2 (T014 / T015).
 *
 * Quatre choses, et rien de plus : l'univers (bascule et mémoire), le
 * méga-menu, le panneau compte, et les pages du tiroir mobile. Tout le reste —
 * ouverture du tiroir, piège de focus, Échap, mini-panier, recherche — reste à
 * Focal et à `jaiio-search.js`, qui le font déjà.
 *
 * Aucun libellé n'est écrit ici : les deux univers sont rendus par le serveur,
 * le script ne fait que dire lequel montrer.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "jaiio_mode";
  var BUYER = "acheteur";
  var SELLER = "vendeur";

  function storeMode(mode) {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (error) {
      /* Navigation privée, stockage refusé : la bascule marche quand même,
         elle ne survit simplement pas à la page suivante. */
    }
  }

  /* ───────────────────────────── Univers ───────────────────────────── */

  /**
   * L'univers est porté par `<html>` : le tiroir sort du `<store-header>` et le
   * bandeau d'annonce est une autre section — une marque de racine les atteint
   * tous sans qu'ils aient à se connaître.
   */
  function applyMode(mode) {
    document.documentElement.setAttribute("data-jaiio-mode", mode);
    document.querySelectorAll('[data-universe-switch]').forEach(function (control) {
      control.setAttribute('data-active', mode);
      control.querySelectorAll('[data-mode-link]').forEach(function (link) {
        if (link.getAttribute('data-mode-link') === mode) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      control.querySelectorAll('[data-mode-set]').forEach(function (button) {
        button.setAttribute('aria-pressed', String(button.getAttribute('data-mode-set') === mode));
      });
    });
  }

  function currentMode() {
    return document.documentElement.getAttribute("data-jaiio-mode") === SELLER ? SELLER : BUYER;
  }

  // Les pages communes ne déduisent pas l'espace de leur chemin. Le href doit
  // aussi le porter pour les nouveaux onglets, sans perdre filtres ni ancre IBAN.
  function prepareAccountLinks() {
    document.querySelectorAll('a[data-account-mode]').forEach(function (link) {
      var url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set(STORAGE_KEY, link.getAttribute('data-account-mode'));
      link.href = url.href;
    });
  }

  /* ───────────────────────── Méga-menu et compte ───────────────────────── */

  function closeMega(header) {
    var open = header.querySelector("[data-mega-trigger][aria-expanded='true']");
    if (open) {
      open.setAttribute("aria-expanded", "false");
      var panel = document.getElementById(open.getAttribute("aria-controls"));
      if (panel) panel.hidden = true;
    }
    var scrim = header.querySelector("[data-mega-scrim]");
    if (scrim) scrim.hidden = true;
    // Focal verrouille la transparence de l'en-tête tant qu'un menu est ouvert.
    header.dispatchEvent(new CustomEvent("desktop-nav:dropdown:close", { bubbles: true }));
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openMega(header, trigger, moveFocus) {
    var panel = document.getElementById(trigger.getAttribute("aria-controls"));
    if (!panel) return;
    closeMega(header);
    closeAccount(header);
    trigger.setAttribute("aria-expanded", "true");
    panel.hidden = false;
    var scrim = header.querySelector("[data-mega-scrim]");
    if (scrim) scrim.hidden = false;
    header.dispatchEvent(new CustomEvent("desktop-nav:dropdown:open", { bubbles: true }));
    // Ouvert au clavier, le panneau prend le focus : il est rendu APRÈS les deux
    // navigations, donc `Tab` sauterait par-dessus sans ce coup de pouce.
    // (Il est rendu là et pas sous sa pill parce que la barre défile
    // horizontalement — un panneau dedans serait rogné.)
    if (moveFocus) {
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    }
  }

  /* Sous 1000 px, le compte ne s'ouvre pas en panneau sous la rangée : il prend
     la page compte du tiroir, plein écran sur téléphone comme `.mdrawer` de
     `Header.dc.html` (retour d'Antoine du 2026-09-30, T071). Le tiroir de Focal
     apporte le verrou du défilement, le piège de focus, Échap et le retour du
     focus au bouton. */
  var MOBILE = window.matchMedia("(max-width: 999px)");

  function drawerParts() {
    var drawer = document.querySelector("[data-drawer]");
    return { drawer: drawer, nav: drawer && drawer.closest("mobile-navigation") };
  }

  // Le compte occupe le tiroir parce qu'on l'a ouvert par l'icône Compte.
  function accountInDrawer() {
    var parts = drawerParts();
    return !!(parts.drawer && parts.drawer.hasAttribute("data-drawer-direct"));
  }

  // Le compte rassemble les deux espaces ; son en-tête reste commun.
  function accountLabel(drawer) {
    var entry = drawer.querySelector('[data-drawer-open="account"]');
    return entry ? entry.getAttribute("data-drawer-label") : "";
  }

  function closeAccount(header) {
    var toggle = header.querySelector("[data-account-toggle]");
    var panel = header.querySelector("[data-account-panel]");
    // Le tiroir se ferme par ses propres moyens (croix, Échap) et remet le
    // bouton à jour à sa fermeture : un clic ou un focus dans le tiroir, qui
    // sort de l'en-tête, ne doit pas le déclarer fermé.
    if (toggle && !accountInDrawer()) toggle.setAttribute("aria-expanded", "false");
    if (panel) panel.hidden = true;
  }

  function openAccountDrawer(toggle) {
    var parts = drawerParts();
    if (!parts.drawer || !parts.nav) return;
    parts.drawer.setAttribute("data-drawer-direct", "");
    showDrawerPage(parts.drawer, "account", accountLabel(parts.drawer));
    toggle.setAttribute("aria-controls", parts.nav.id);
    toggle.setAttribute("aria-expanded", "true");
    parts.nav.open = true;
  }

  function toggleAccount(header) {
    var toggle = header.querySelector("[data-account-toggle]");
    var panel = header.querySelector("[data-account-panel]");
    if (!toggle || !panel) return;
    closeMega(header);
    if (MOBILE.matches) {
      panel.hidden = true;
      openAccountDrawer(toggle);
      return;
    }
    var willOpen = panel.hidden;
    closeAccount(header);
    if (!willOpen) return;
    toggle.setAttribute("aria-controls", panel.id);
    toggle.setAttribute("aria-expanded", "true");
    panel.hidden = false;
  }

  /* ───────────────────────── Pages du tiroir ───────────────────────── */

  function showDrawerPage(drawer, pageId, label) {
    drawer.querySelectorAll("[data-drawer-page]").forEach(function (page) {
      page.hidden = page.getAttribute("data-drawer-page") !== pageId;
    });
    drawer.setAttribute("data-drawer-current", pageId);
    var isRoot = pageId === "root";
    // Compte ouvert par l'icône : un titre à la place du retour, il n'y a pas
    // de page d'où l'on vient dans le tiroir.
    var direct = pageId === "account" && drawer.hasAttribute("data-drawer-direct");
    var back = drawer.querySelector("[data-drawer-back]");
    var backLabel = drawer.querySelector("[data-drawer-back-label]");
    var title = drawer.querySelector("[data-drawer-title]");
    var rootOnly = drawer.querySelector("[data-drawer-root-only]");
    if (back) back.hidden = isRoot || direct;
    // Vidé à la racine : le bouton caché ne garde pas le nom de la page quittée.
    if (backLabel) backLabel.textContent = isRoot || direct ? "" : label || "";
    if (title) {
      title.hidden = !direct;
      title.textContent = direct ? label || "" : "";
    }
    if (rootOnly) rootOnly.hidden = !isRoot;
    // Une page ouverte commence en haut : on ne reprend pas le défilement de la
    // précédente, qui n'a rien à voir.
    var list = drawer.querySelector("[data-drawer-page]:not([hidden]) .jh-mnav");
    if (list) list.scrollTop = 0;
  }

  /* ───────────────────────────── Câblage ───────────────────────────── */

  // Sur les collections, le header disparaît à la descente. Les filtres
  // restent complets sur desktop ; seule l'entrée compacte reste sur mobile.
  // La remontée n'a aucun seuil : elle rend aussitôt la navigation disponible.
  function initCollectionScroll(header, bar, section) {
    var root = document.documentElement;
    var marker = document.createElement('span');
    marker.setAttribute('aria-hidden', 'true');
    marker.style.cssText = 'display:block;inline-size:0;block-size:0';
    bar.before(marker);
    root.setAttribute('data-jaiio-collection-scroll', '');
    var lastY = Math.max(0, window.scrollY), ticking = false, collapsed = false;
    var announcement = document.querySelector('.shopify-section--announcement-bar');

    function render() {
      root.toggleAttribute('data-jaiio-header-hidden', collapsed);
      section.inert = collapsed;
      if (announcement) announcement.inert = collapsed;
      var filtersCollapsed = collapsed && !matchMedia('(min-width: 1000px)').matches;
      bar.classList.toggle('is-scroll-collapsed', filtersCollapsed);
      // La barre native remplace son contenu après chaque filtrage : relire
      // les contrôles plutôt que conserver des références aux anciens nœuds.
      var selectors = bar.querySelector('[data-scroll-filter-controls]');
      var toolbar = bar.querySelector('[data-scroll-filter-toolbar]');
      if (selectors) selectors.inert = filtersCollapsed;
      if (toolbar) toolbar.hidden = !filtersCollapsed;
    }
    function interacting() {
      var active = document.activeElement;
      return header.contains(active) || header.querySelector('[aria-expanded="true"]') ||
        bar.querySelector('[data-panel]:not([hidden]), [data-sheet]:not([hidden])') ||
        (bar.contains(active) && active.matches(':focus-visible'));
    }
    function update() {
      ticking = false;
      var y = Math.max(0, window.scrollY), delta = y - lastY;
      lastY = y;
      var styles = getComputedStyle(root);
      var offset = (parseFloat(styles.getPropertyValue('--header-height')) || 0) +
        (parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0) *
        (parseFloat(styles.getPropertyValue('--enable-sticky-announcement-bar')) || 0);
      var pinned = y > 0 && marker.getBoundingClientRect().top <= offset;
      var wasPinned = bar.classList.contains('is-scroll-compact');
      bar.classList.toggle('is-scroll-compact', pinned);
      if (!pinned || delta < 0 || interacting()) collapsed = false;
      else if (delta > 0 || !wasPinned) collapsed = true;
      render();
    }
    function schedule() {
      if (ticking) return;
      ticking = true; requestAnimationFrame(update);
    }
    bar.addEventListener('click', function (event) {
      var action = event.target.closest('[data-scroll-filter-action]');
      if (!action) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      collapsed = false;
      render();
      var desktop = matchMedia('(min-width: 1000px)').matches;
      var sort = action.getAttribute('data-scroll-filter-action') === 'sort';
      var selector = desktop ? (sort ? '.facet-bar__pills [aria-controls="facet-panel-sort"]' : '.facet-bar__pills button') :
        (sort ? '.facet-bar__mobile-toolbar [data-sheet-target]' : '.facet-bar__mobile-toolbar button');
      var trigger = bar.querySelector(selector);
      if (trigger) {
        trigger.focus({ preventScroll: true });
        // Les panneaux restent ceux du moteur. Leur déclencheur visible sert
        // aussi d'ancre et reçoit le focus à la fermeture, sans saut de page.
        if (!desktop || sort) trigger.click();
      }
    }, true);
    bar.addEventListener('focusout', schedule);
    header.addEventListener('focusin', function () { collapsed = false; render(); });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    new MutationObserver(render).observe(bar, { childList: true, subtree: true });
    update();
  }

  // La recherche garde son comportement desktop existant.
  function initListScroll(header) {
    var bar = document.querySelector('facet-bar, facet-bar-engine');
    var section = header.closest('.shopify-section');
    var store = header.closest('store-header');
    if (!bar || !section || !store || !store.hasAttribute('sticky')) return;
    if (bar.getAttribute('data-context') === 'collection') {
      initCollectionScroll(header, bar, section);
      return;
    }
    var marker = document.createElement('span');
    marker.setAttribute('aria-hidden', 'true');
    marker.style.cssText = 'display:block;inline-size:0;block-size:0';
    bar.before(marker);
    var lastY = Math.max(0, window.scrollY), distance = 0, ticking = false, settleUntil = 0;
    function reveal(hidden) {
      document.documentElement.toggleAttribute('data-jaiio-header-hidden', hidden);
      section.inert = hidden;
    }
    function update() {
      ticking = false;
      var y = Math.max(0, window.scrollY), delta = y - lastY;
      lastY = y;
      if (!matchMedia('(min-width: 1000px)').matches) {
        reveal(false); bar.classList.remove('is-scroll-compact'); distance = 0; return;
      }
      var styles = getComputedStyle(document.documentElement);
      var offset = (parseFloat(styles.getPropertyValue('--header-height')) || 0) +
        (parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0) *
        (parseFloat(styles.getPropertyValue('--enable-sticky-announcement-bar')) || 0);
      var compact = marker.getBoundingClientRect().top <= offset - 8;
      var chips = bar.querySelector('.facet-bar__chips');
      if (chips && chips.contains(document.activeElement)) compact = false;
      var compactChanged = bar.classList.contains('is-scroll-compact') !== compact;
      if (compactChanged) {
        bar.classList.toggle('is-scroll-compact', compact);
        // Le navigateur peut compenser le retrait des chips par l'ancrage du
        // scroll. Cette compensation n'est pas un geste vers le haut.
        settleUntil = performance.now() + 220;
      }
      if (!compact || header.contains(document.activeElement) || header.querySelector('[aria-expanded="true"]')) {
        reveal(false); distance = 0; return;
      }
      if (compactChanged && delta >= 40) { reveal(true); distance = 0; return; }
      if (performance.now() < settleUntil || delta === 0) return;
      if ((delta > 0) !== (distance > 0)) distance = 0;
      distance += delta;
      if (distance >= 40) reveal(true);
      else if (distance <= -20) reveal(false);
    }
    function schedule() {
      if (ticking) return;
      ticking = true; requestAnimationFrame(update);
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    bar.addEventListener('focusout', schedule);
    header.addEventListener('focusin', function () { reveal(false); });
    update();
  }

  // Sous 360 px seulement : replier la bande, sans réduire les cibles.
  function initUniverseBand(header) {
    var band = header.querySelector('[data-universe-band]');
    var store = header.closest('store-header');
    if (!band || !store || !store.hasAttribute('sticky')) return;
    var lastY = Math.max(0, window.scrollY), distance = 0, ticking = false, settleUntil = 0;
    function reveal(collapsed) {
      if (band.classList.contains('is-collapsed') === collapsed) return;
      band.classList.toggle('is-collapsed', collapsed);
      // La réduction du header peut déplacer l'ancre de scroll : cette
      // compensation du navigateur ne constitue pas un geste inverse.
      settleUntil = performance.now() + 280;
    }
    function update() {
      ticking = false;
      var y = Math.max(0, window.scrollY), delta = y - lastY;
      lastY = y;
      if (!matchMedia('(max-width: 359px)').matches || y < 8 || band.contains(document.activeElement)) {
        reveal(false); distance = 0; return;
      }
      if (performance.now() < settleUntil) return;
      if ((delta > 0) !== (distance > 0)) distance = 0;
      distance += delta;
      if (distance >= 8) reveal(true);
      else if (distance <= -8) reveal(false);
    }
    function schedule() {
      if (ticking) return;
      ticking = true; requestAnimationFrame(update);
    }
    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule);
    // Garder les liens repliés dans l'ordre de tabulation : le focus révèle
    // la bande avant que l'utilisatrice interagisse avec un lien invisible.
    band.addEventListener('focusin', function () { band.classList.remove('is-collapsed'); });
  }

  function init() {
    var header = document.querySelector("jaiio-header");
    if (!header) return;

    // Le choix de départ est déjà posé par le script en tête de section, avant le
    // premier rendu — ici on ne fait que garder les boutons cohérents.
    applyMode(currentMode());
    prepareAccountLinks();
    initListScroll(header);
    initUniverseBand(header);

    document.addEventListener("click", function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var drawerRoot = target.closest("[data-drawer]");

      var modeLink = target.closest('[data-mode-link], [data-account-mode]');
      var modeButton = target.closest('[data-mode-set]');
      if (modeLink || modeButton) {
        // Respecter les nouveaux onglets et clics modifiés. Le lien garde sa
        // navigation native : l'animation ne retarde jamais le changement de page.
        if (modeLink && (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
        var next = modeLink ? modeLink.getAttribute('data-mode-link') || modeLink.getAttribute('data-account-mode') : modeButton.getAttribute('data-mode-set');
        storeMode(next);
        applyMode(next);
        closeMega(header);
        closeAccount(header);
        if (modeButton && drawerRoot) {
          if (drawerRoot.getAttribute('data-drawer-current') === 'account') showDrawerPage(drawerRoot, 'account', accountLabel(drawerRoot));
          else showDrawerPage(drawerRoot, 'root');
        }
        return;
      }

      var megaTrigger = target.closest("[data-mega-trigger]");
      if (megaTrigger) {
        // Les catégories sont de vrais liens : clic / Entrée suivent leur href.
        if (megaTrigger.tagName === "A") {
          closeMega(header);
          return;
        }
        if (megaTrigger.getAttribute("aria-expanded") === "true") closeMega(header);
        // `detail === 0` : le clic ne vient pas d'un pointeur, c'est Entrée ou
        // Espace sur le bouton — donc quelqu'un qui navigue au clavier.
        else openMega(header, megaTrigger, event.detail === 0);
        return;
      }

      if (target.closest("[data-account-toggle]")) {
        toggleAccount(header);
        return;
      }

      var drawerOpen = target.closest("[data-drawer-open]");
      if (drawerOpen && drawerRoot) {
        drawerRoot.removeAttribute("data-drawer-direct");
        showDrawerPage(drawerRoot, drawerOpen.getAttribute("data-drawer-open"), drawerOpen.getAttribute("data-drawer-label"));
        return;
      }

      if (target.closest("[data-drawer-back]") && drawerRoot) {
        showDrawerPage(drawerRoot, "root");
        return;
      }

      if (target.closest("[data-mega-scrim]")) {
        closeMega(header);
        return;
      }

      // Clic hors de l'en-tête : les panneaux se referment. Le tiroir est hors
      // de l'en-tête (append-body) mais n'est pas « à côté » : Focal le gère.
      if (!target.closest("jaiio-header") && !drawerRoot) {
        closeMega(header);
        closeAccount(header);
      }
    });

    // Flèche bas ouvre le sous-menu au clavier ; Entrée garde la navigation.
    header.addEventListener("keydown", function (event) {
      var trigger = event.target.closest && event.target.closest("[data-mega-trigger]");
      if (event.key !== "ArrowDown" || !trigger) return;
      event.preventDefault();
      openMega(header, trigger, true);
    });

    // Survol : la navigation desktop s'ouvre au passage, comme la maquette.
    header.addEventListener("mouseover", function (event) {
      if (!window.matchMedia("(min-width: 1000px)").matches) return;
      var trigger = event.target.closest && event.target.closest("[data-mega-trigger]");
      if (trigger) {
        openMega(header, trigger);
        return;
      }
      if (event.target.closest && event.target.closest("[data-mega-close]")) closeMega(header);
    });

    header.addEventListener("mouseleave", function () {
      if (!window.matchMedia("(min-width: 1000px)").matches) return;
      closeMega(header);
    });

    // Le focus quitte l'en-tête (Tab après le dernier lien du panneau) : on
    // referme, sinon le panneau resterait ouvert derrière la page.
    header.addEventListener("focusout", function (event) {
      if (event.relatedTarget && header.contains(event.relatedTarget)) return;
      closeMega(header);
      closeAccount(header);
    });

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      var openTrigger = header.querySelector("[data-mega-trigger][aria-expanded='true']");
      var accountToggle = header.querySelector("[data-account-toggle][aria-expanded='true']");
      if (openTrigger) {
        closeMega(header);
        openTrigger.focus();
      }
      if (accountToggle && !accountInDrawer()) {
        closeAccount(header);
        accountToggle.focus();
      }
    });

    // Le tiroir se referme sur la page où on l'a laissé : il rouvre à la racine.
    var drawer = document.querySelector("[data-drawer]");
    if (drawer) {
      var panel = drawer.closest("mobile-navigation");
      if (panel) {
        panel.addEventListener("mobile-nav:close", function () {
          drawer.removeAttribute("data-drawer-direct");
          showDrawerPage(drawer, "root");
          var toggle = header.querySelector("[data-account-toggle]");
          if (toggle) toggle.setAttribute("aria-expanded", "false");
        });
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
